/**
 * Track evaluator
 *
 * Pure, store-free logic for deciding whether an item belongs to a custom
 * Track defined by inclusion conditions (definitionMode ===
 * 'conditions'). Dimension-mode tracks partition items by shared values and are
 * handled where lanes are generated; this module covers the predicate path —
 * the "1..n things that must be true for an item to be included".
 *
 * Kept dependency-free so it can run in the admin preview, at cycle creation,
 * and in unit tests without pulling in stores.
 */

import type {
  CustomBaselineDef,
  FrameCondition,
  FrameConditionOperator,
} from '../types/prioritization/tenant';

/**
 * Minimal structural shape of an item this evaluator can read. Both `Item`
 * and `ShapedItem` satisfy it. Only the fields the system dimensions map to
 * are referenced, plus the `attributes` bag for scoring/custom fields.
 */
export interface FrameEvaluableItem {
  itemCategory?: string | null;
  altitudeLevel?: number | string | null;
  /** @deprecated legacy altitude string — read as a fallback only */
  altitude?: string | null;
  queueId?: string | null;
  source?: string | null;
  exit_status?: string | null;
  attributes?: Array<{ attributeKey: string; value: unknown }>;
  /** Generic custom field bag, if present on the item row. */
  customFields?: Record<string, unknown> | null;
  [key: string]: unknown;
}

/** Keys that resolve to first-class item fields rather than the attribute bag. */
const SYSTEM_FIELD_KEYS = new Set([
  'itemCategory',
  'altitude',
  'queue',
  'source',
  'exit_status',
]);

/**
 * Resolve the value of a frame `key` on an item. System keys map to dedicated
 * fields; everything else is looked up in the `attributes` bag first, then a
 * generic `customFields` record, then a direct property of the row.
 */
export function resolveItemFieldValue(item: FrameEvaluableItem, key: string): unknown {
  if (SYSTEM_FIELD_KEYS.has(key)) {
    switch (key) {
      case 'itemCategory':
        return item.itemCategory ?? null;
      case 'altitude':
        // altitudeLevel is authoritative; fall back to legacy string.
        return item.altitudeLevel ?? item.altitude ?? null;
      case 'queue':
        return item.queueId ?? null;
      case 'source':
        return item.source ?? null;
      case 'exit_status':
        return item.exit_status ?? null;
    }
  }

  const fromAttributes = item.attributes?.find((a) => a.attributeKey === key);
  if (fromAttributes !== undefined) return fromAttributes.value;

  if (item.customFields && key in item.customFields) return item.customFields[key];

  if (key in item) return (item as Record<string, unknown>)[key];

  return undefined;
}

// ── Value helpers ─────────────────────────────────────────────────────────────

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/** Loose, case-insensitive equality across string/number/boolean operands. */
function looseEquals(a: unknown, b: unknown): boolean {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (typeof a === 'number' || typeof b === 'number') {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return toBoolean(a) === toBoolean(b);
  }
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  const s = String(value).trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
}

/** Compare two operands numerically, falling back to date, then string. */
function compare(a: unknown, b: unknown): number | null {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;

  const da = Date.parse(String(a));
  const db = Date.parse(String(b));
  if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;

  const sa = String(a);
  const sb = String(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}

/** Normalize an item value into the set of atomic strings it contains. */
function toValueSet(value: unknown): string[] {
  if (isEmpty(value)) return [];
  if (Array.isArray(value)) return value.map((v) => String(v).trim().toLowerCase());
  return [String(value).trim().toLowerCase()];
}

// ── Condition evaluation ────────────────────────────────────────────────────────

/** Operators that compare an item value against a single operand. */
const SINGLE_OPERAND_OPS: FrameConditionOperator[] = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
];

/**
 * Evaluate a single condition against an already-resolved item field value.
 * Unset values fail every operator except `is_not_set` (and `neq`, which a
 * missing value trivially satisfies).
 */
export function evaluateConditionValue(value: unknown, condition: FrameCondition): boolean {
  const { operator } = condition;

  if (operator === 'is_set') return !isEmpty(value);
  if (operator === 'is_not_set') return isEmpty(value);

  // From here on a missing value can only satisfy `neq`.
  if (isEmpty(value)) return operator === 'neq';

  switch (operator) {
    case 'eq':
      return looseEquals(value, condition.value);
    case 'neq':
      return !looseEquals(value, condition.value);
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const c = compare(value, condition.value);
      if (c === null) return false;
      if (operator === 'gt') return c > 0;
      if (operator === 'gte') return c >= 0;
      if (operator === 'lt') return c < 0;
      return c <= 0; // lte
    }
    case 'between': {
      const lo = compare(value, condition.value);
      const hi = compare(value, condition.value2);
      return lo !== null && hi !== null && lo >= 0 && hi <= 0;
    }
    case 'in':
    case 'not_in': {
      const operands = (condition.values ?? []).map((v) => v.trim().toLowerCase());
      const haystack = toValueSet(value);
      const hit = haystack.some((v) => operands.includes(v));
      return operator === 'in' ? hit : !hit;
    }
    case 'contains': {
      const needle = String(condition.value ?? '').trim().toLowerCase();
      if (needle === '') return false;
      if (Array.isArray(value)) return toValueSet(value).includes(needle);
      return String(value).toLowerCase().includes(needle);
    }
    default:
      return false;
  }
}

/** Resolve the field then evaluate the condition against an item. */
export function evaluateCondition(item: FrameEvaluableItem, condition: FrameCondition): boolean {
  return evaluateConditionValue(resolveItemFieldValue(item, condition.key), condition);
}

/**
 * Whether `condition` is fully specified enough to evaluate. Operands are
 * required for value-comparing operators; presence operators never need one.
 */
export function isConditionComplete(condition: FrameCondition): boolean {
  if (!condition.key) return false;
  const { operator } = condition;
  if (operator === 'is_set' || operator === 'is_not_set') return true;
  if (operator === 'between') {
    return condition.value !== undefined && condition.value !== '' &&
      condition.value2 !== undefined && condition.value2 !== '';
  }
  if (operator === 'in' || operator === 'not_in') {
    return (condition.values?.filter((v) => v.trim() !== '').length ?? 0) > 0;
  }
  if (SINGLE_OPERAND_OPS.includes(operator)) {
    return condition.value !== undefined && condition.value !== '';
  }
  return false;
}

/**
 * Whether an item is included by a custom frame's inclusion conditions.
 *
 * Only meaningful for `definitionMode === 'conditions'`. Incomplete conditions
 * are ignored. With no complete conditions the frame includes nothing (an empty
 * predicate set is treated as "undefined membership", not "match all").
 */
export function itemMatchesFrameConditions(
  item: FrameEvaluableItem,
  frame: Pick<CustomBaselineDef, 'conditions' | 'match'>,
): boolean {
  const conditions = (frame.conditions ?? []).filter(isConditionComplete);
  if (conditions.length === 0) return false;

  const match = frame.match ?? 'all';
  return match === 'any'
    ? conditions.some((c) => evaluateCondition(item, c))
    : conditions.every((c) => evaluateCondition(item, c));
}
